
import { Hotspot, Configurator } from "threngine";
import { CarModelConfig, ModelLoadController, CarModelHeadlineHotspotInfo, CarModelInteractionHotspotInfo } from "./ModelLoad/ModelLoadController";
import { Controller3D } from "./3DController";
import { SelectionController } from "./SelectionController";
import {EventEmitter} from "events";
import { RenderEngine } from "./RenderEngine";
import axios from "axios";
import { ConfiguratorModel } from "./ConfiguratorModel";

import {
  Scene,
  Quaternion,
  Vector3
} from "three";
import { Hotspot3D } from "./Hotspot3D";
import { getOpacity } from "./Hotspot3DOpacity";
import { AnimationController } from "./AnimationController";
import { isMobile } from "react-device-detect";

export class HotspotController {
  private configurator:Configurator;
  private carModelConfig:CarModelConfig;
  
  private _hotspotsVisible:boolean = false;
  private _hotspotBinding:any = {};
  private _hotspotBindingClose:()=>void = ()=>{};
  private _hotspots:Hotspot[] = [];
  private _animatingDoorFL = false;
  private _animatingDoorFR = false;
  private _animatingDoorRL = false;
  private _animatingDoorRR = false;
  private _animatingTrunkLid = false;
  private _animatingCupHolder = false;
  private _presentation = false;

  private _hotspotScene:Scene = new Scene();

  public static event = new EventEmitter();

  public get visible():boolean { return this._hotspotsVisible; }

  public get hotspotScene():Scene { return this._hotspotScene; }

  public constructor(
    private engine:RenderEngine,
    private controller3D:Controller3D,
    private selectionController:SelectionController,
    private animationController:AnimationController,
    private model:ConfiguratorModel
  ) {
    this.configurator = engine.configurator as Configurator;

    Controller3D.event.addListener(Controller3D.EVENT_INTERIOR_EXTERIOR, this._updateHotspotVisibility);

    Controller3D.event.addListener("animationDoorFLStart", () => {
      this._animatingDoorFL = true;
      this._updateHotspotVisibility();
    });
    Controller3D.event.addListener("animationDoorFLEnd", () => {
      this._animatingDoorFL = false;
      this._updateHotspotVisibility();
    });

    Controller3D.event.addListener("animationDoorFRStart", () => {
      this._animatingDoorFR = true;
      this._updateHotspotVisibility();
    });
    Controller3D.event.addListener("animationDoorFREnd", () => {
      this._animatingDoorFR = false;
      this._updateHotspotVisibility();
    });
    
    Controller3D.event.addListener("animationDoorRLStart", () => {
      this._animatingDoorRL = true;
      this._updateHotspotVisibility();
    });
    Controller3D.event.addListener("animationDoorRLEnd", () => {
      this._animatingDoorRL = false;
      this._updateHotspotVisibility();
    });
    
    Controller3D.event.addListener("animationDoorRRStart", () => {
      this._animatingDoorRR = true;
      this._updateHotspotVisibility();
    });
    Controller3D.event.addListener("animationDoorRREnd", () => {
      this._animatingDoorRR = false;
      this._updateHotspotVisibility();
    });

    Controller3D.event.addListener("animationTrunkLidStart", () => {
      this._animatingTrunkLid = true;
      this._updateHotspotVisibility();
    });
    Controller3D.event.addListener("animationTrunkLidEnd", () => {
      this._animatingTrunkLid = false;
      this._updateHotspotVisibility();
    });

    Controller3D.event.addListener("animationCupHolderStart", () => {
      this._animatingCupHolder = true;
      this._updateHotspotVisibility();
    });
    Controller3D.event.addListener("animationCupHolderEnd", () => {
      this._animatingCupHolder = false;
      this._updateHotspotVisibility();
    });
    
    let didHideHotspotsForLoad = false;

    ModelLoadController.event.addListener(ModelLoadController.STATUS_LOADED, (config:CarModelConfig) => {
      this.carModelConfig = config;

      // TODO: would be nice if we could handle this in the EVENT_ANIMATION_START_STOP handler.
      if (this._hotspotsVisible) {
        didHideHotspotsForLoad = true;
        this.hideHotspots();
      } else {
        didHideHotspotsForLoad = false;
      }

      this.configurator = this.engine.configurator as Configurator;
      this._hotspots = this.engine.configurator.config.hotspots.filter(
        hotspot => this.isActiveHotspot(hotspot));

      this._createHotspots3D();
      // (<any>this.configurator)._scene.add(this.hotspotScene); // REMOVE ME
      this.engine.hotspotRenderPass.hotspotScene = this.hotspotScene;
    });
  
    // Only show the hotspots once we're done with the intro driving animation.
    ModelLoadController.event.addListener(ModelLoadController.EVENT_ANIMATION_START_STOP, (running:boolean) => {
      if (!running && didHideHotspotsForLoad) {
        this.showHotspots();
      }
    });

    // Take care of opacity for presentation-style hotspots
    const renderService = this.engine.engine.services.render;
    renderService.onAfterRender.subscribe(async () => {
      if (this._presentation) {
        for (const hotspot of this._hotspots) {
          const element = document.getElementById(hotspot.id) as HTMLDivElement;

          if (element) {
            const span = element.getElementsByTagName("span")[0];
          
            if (span) {
              span.style.opacity = Math.pow(parseFloat(element.style.opacity), 3).toString();
            }
          }
        }
      }
    });

    this.engine.engine.services.render.onBeforeRender.subscribe(this._onBeforeRender3D);
  }

  private isInteriorHotspot(hotspot:Hotspot):boolean {
    return this.carModelConfig.hotspots.interior.indexOf(hotspot.name) != -1;
  }

  private isExteriorHotspot(hotspot:Hotspot):boolean {
    return this.carModelConfig.hotspots.exterior.indexOf(hotspot.name) != -1;
  }

  private isActiveHotspot(hotspot:Hotspot):boolean {
    return this.isInteriorHotspot(hotspot) || this.isExteriorHotspot(hotspot);
  }

  private isHeadlineHotspot(hotspot:Hotspot):boolean {
    return this.carModelConfig.hotspots.headline.map(e => e.name).indexOf(hotspot.name) != -1;
  }

  private isInteractionHotspot(hotspot:Hotspot):boolean {
    return this.carModelConfig.hotspots.interaction.map(e => e.name).indexOf(hotspot.name) != -1;
  }

  private _updateHotspotVisibility = ():void => {
    const interior = this.controller3D.isInterior();
    if (!this._hotspotsVisible) {
      return;
    }
    for (const hotspot of this._hotspots) {
      const element = document.getElementById(hotspot.id);
      if (!element) {
        continue;
      }
      let visible = (
        this.isInteriorHotspot(hotspot) ==  interior ||
        this.isExteriorHotspot(hotspot) == !interior) && !this.controller3D.isGroundHotspot();

      const predicate = this.carModelConfig.hotspots.visibility[hotspot.name];
      
      if (predicate) {
        switch (predicate) {
          case "door_fl_opened":
            visible &&=  (this.controller3D.doorFrontLeftOpen && !this._animatingDoorFL);
            break;
          case "door_fl_closed":
            visible &&= (!this.controller3D.doorFrontLeftOpen && !this._animatingDoorFL);
            break;

          case "door_fr_opened":
            visible &&=  (this.controller3D.doorFrontRightOpen && !this._animatingDoorFR);
            break;
          case "door_fr_closed":
            visible &&= (!this.controller3D.doorFrontRightOpen && !this._animatingDoorFR);
            break;

          case "door_rl_opened":
            visible &&=  (this.controller3D.doorRearLeftOpen && !this._animatingDoorRL);
            break;
          case "door_rl_closed":
            visible &&= (!this.controller3D.doorRearLeftOpen && !this._animatingDoorRL);
            break;

          case "door_rr_opened":
            visible &&=  (this.controller3D.doorRearRightOpen && !this._animatingDoorRR);
            break;
          case "door_rr_closed":
            visible &&= (!this.controller3D.doorRearRightOpen && !this._animatingDoorRR);
            break;
          
          case "door_trunklid_opened":
             visible &&=  (this.controller3D.trunkLidOpen && !this._animatingTrunkLid);
            break;
          case "door_trunklid_closed":
            visible &&= (!this.controller3D.trunkLidOpen && !this._animatingTrunkLid);
            break;
          
          case "door_cupholder_opened":
            visible &&=  (this.controller3D.cupHolderOpen && !this._animatingCupHolder);
            break;
          case "door_cupholder_closed":
            visible &&= (!this.controller3D.cupHolderOpen && !this._animatingCupHolder);
            break;
      
          default:
            console.warn("HotspotController: unhandled interaction hotspot visibility");
            break;
        }
      }

      if (visible) {
        const visibleOld = element.style.display == "inherit";
        // Prevent the hotspot click animation to retrigger when we show a previously hidden hotspot.
        if (!visibleOld && element.classList.contains("hotspot-active")) {
          element.classList.remove("hotspot-active");
        }
        element.style.display = "inherit";
      } else {
        element.style.display = "none";
      }

      element.style.display = "none"; // REMOVE ME

      // Update 3D hotspot visibility
      const hotspot3D = this.hotspotScene.getObjectByName(hotspot.id);
      if (hotspot3D) {
        hotspot3D.visible = visible;
      }
    }
  }

  public openHotspot(name:string):void {
    this._hotspotBinding[name]();
  }

  public closeHotspot():void {
    this._hotspotBindingClose();
  }

  public hideHotspots() {
    this.configurator.setHotspots([]);
    this._presentation = false;
    this._hotspotsVisible = false;
    
    this.selectionController.hideGroundHotspots();

    this._hideHotspots3D();
  }

  public showHotspots() {
    if (this._hotspotsVisible) {
      return;
    }

    this.configurator.setHotspots(this._hotspots.map(hotspot => hotspot.id));
    
    for (const hotspot of this._hotspots) {
      const element = document.getElementById(hotspot.id) as HTMLDivElement;
      element.style.background = '';
      element.innerText = '';
      if (this.isHeadlineHotspot(hotspot)) {
        const hotspotInfo = this.carModelConfig.hotspots.headline.find(info => info.name === hotspot.name);
        this._createHeadlineHotspotGraphic(element, hotspotInfo);
      } else if (this.isInteractionHotspot(hotspot)) {
        const hotspotInfo = this.carModelConfig.hotspots.interaction.find(info => info.name === hotspot.name);
        this._createInteractionHotspotGraphic(element, hotspotInfo)
      } else {
        this._createRegularHotspotGraphic(element);
      }

      element.addEventListener('click', (event:MouseEvent) => {
        if (!this.isHeadlineHotspot(hotspot)) {
          const x0 = parseFloat(element.style.left.replace("px", ""));;
          const y0 = parseFloat(element.style.top.replace("px", ""));
          const x1 = event.clientX;
          const y1 = event.clientY;
          const distanceSqr = Math.pow(x1 - x0, 2) + Math.pow(y1 - y0, 2);
          if (distanceSqr > 512)
            return;
        }

        event.stopPropagation();
        // TODO: do this only for non-headline hotspots
        element.classList.remove('hotspot-active'); // retrigger animation

        const callback = this._hotspotBinding[hotspot.name];
        if (callback) {
          setTimeout(() => callback(true), 500);
        }
        setTimeout(() => element.classList.add('hotspot-active'), 32);
      })
    }

    this._hotspotsVisible = true;
    this._presentation = false;
    this._updateHotspotVisibility();

    this.selectionController.showGroundHotspots();

    this._showHotspots3D();
  }

  public async showPresentationHotspots():Promise<void> {
    if (this._hotspotsVisible) {
      this.hideHotspots();
    }

    const hotspots = this._hotspots.filter(h => !this.isInteractionHotspot(h) && !this.isHeadlineHotspot(h));
    const hotspotData = (await loadHotspots()).find(e => e.car == this.model.modelLoadController.currentModelName).data;

    console.log("hotspot data", hotspotData);

    this.configurator.setHotspots(hotspots.map(h => h.id));

    for (const hotspot of hotspots) {
      // TODO: should we use e.hotspotName or e.name?
      const data = hotspotData.find(e => e.hotspotName == hotspot.name);

      const element = document.getElementById(hotspot.id) as HTMLDivElement;
      element.style.background = '';
      element.innerText = '';

      this._createRegularHotspotGraphic(element);

      const span = document.createElement("span");
      span.innerText = data.content[0].header;
      span.style.fontFamily = "Volks";
      span.style.fontSize = "20px";
      span.style.color = "white";
      span.style.position = "absolute";
      span.style.top = "0px";
      span.style.left = "32px";
      span.style.marginLeft = "5px"
      span.style.whiteSpace = "nowrap";;
      span.style.textShadow = "0 0 5px black";
      element.appendChild(span);
    }

    // hmm
    this._hotspotsVisible = true;
    this._presentation = true;
    this._updateHotspotVisibility();
  }

  private _showHotspots3D():void {
    this.hotspotScene.visible = true;
  }

  private _hideHotspots3D():void {
    this.hotspotScene.visible = false; 
  }

  private _createHotspots3D():void {
    // Remove and existing, possibly stale, 3D hotspots.
    this.hotspotScene.children.slice().forEach(c => {
      this.hotspotScene.remove(c);
    });

    for (const hotspot of this._hotspots) {
      if (!this.isHeadlineHotspot(hotspot)) {
        const interaction = this.isInteractionHotspot(hotspot);

        const hotspot3D = new Hotspot3D(interaction, this.animationController, interaction ? undefined : () => {
          const callback = this._hotspotBinding[hotspot.name];
          if (callback) {
            callback(true);
          }
        });

        hotspot3D.position.set(hotspot.position[0], hotspot.position[1], hotspot.position[2]);
        hotspot3D.name = hotspot.id;
        hotspot3D.visible = false;
        this.hotspotScene.add(hotspot3D);
      }
    }
  }

  private _onBeforeRender3D = async () => {
    // Get world-space rotation of the scene
    const tmp = new Vector3();
    const worldRotation = new Quaternion();
    this.hotspotScene.matrixWorld.decompose(tmp, worldRotation, tmp);
    
    // Ugh... end me please.
    // We want the camera that we are *actually* rendering with,
    // so we have to do this mess!
    const composer = this.engine.engine.services.render.renderController.composer;
    const camera = composer.saoRenderer.camera;

    // Invert world-space rotation and apply camera rotation 
    // to get the appropriate local-space rotation for each hotspot.
    const hotspotRotation = worldRotation.clone();
    hotspotRotation.invert();
    hotspotRotation.multiply(camera.quaternion);

    for (const hotspot3D of this.hotspotScene.children) {
      const hotspot = this._hotspots.find(h => h.id == hotspot3D.name);
      
      (<Hotspot3D>hotspot3D).opacity = getOpacity(hotspot, <Hotspot3D>hotspot3D, camera, worldRotation);
      
      hotspot3D.quaternion.copy(hotspotRotation);

      const viewPosition = new Vector3().setFromMatrixPosition(hotspot3D.matrixWorld).applyMatrix4(camera.matrixWorldInverse); 
      const desiredWidth = isMobile ? 96 : 128;
      const screenSpaceWidth = (
        new Vector3(0.5 * Hotspot3D.RADIUS, 0, viewPosition.z).applyMatrix4(camera.projectionMatrix)
      ).x * 2 * window.innerWidth;
      const scale = desiredWidth / screenSpaceWidth;

      (<Hotspot3D>hotspot3D).size = scale / this.hotspotScene.scale.x;
    }
  };

  private _createRegularHotspotGraphic(parent:HTMLDivElement):void {
    parent.classList.add('hotspot')

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg") as SVGElement;
    svg.setAttribute("width", "96");
    svg.setAttribute("height", "96");
    svg.style.margin = "-36px 0 0 -36px";
    svg.style.transform = "scale(0.85)";
      
    const blur0 = document.createElementNS(svgNS, "filter");
    blur0.setAttribute("id", "blur0");
    blur0.setAttribute("x", "-50%");
    blur0.setAttribute("y", "-50%");
    blur0.setAttribute("width", "200%");
    blur0.setAttribute("height", "200%");
    const feGuassianBlur0 = document.createElementNS(svgNS, "feGaussianBlur");
    feGuassianBlur0.setAttribute("stdDeviation", "2");
    blur0.appendChild(feGuassianBlur0);

    const blur1 = document.createElementNS(svgNS, "filter");
    blur1.setAttribute("id", "blur1");
    blur1.setAttribute("x", "-50%");
    blur1.setAttribute("y", "-50%");
    blur1.setAttribute("width", "200%");
    blur1.setAttribute("height", "200%");
    const feGuassianBlur1 = document.createElementNS(svgNS, "feGaussianBlur");
    feGuassianBlur1.setAttribute("stdDeviation", "8");
    blur1.appendChild(feGuassianBlur1);

    const blur2 = document.createElementNS(svgNS, "filter");
    blur2.setAttribute("id", "blur2");
    blur2.setAttribute("x", "-50%");
    blur2.setAttribute("y", "-50%");
    blur2.setAttribute("width", "200%");
    blur2.setAttribute("height", "200%");
    const feGuassianBlur2 = document.createElementNS(svgNS, "feGaussianBlur");
    feGuassianBlur2.setAttribute("stdDeviation", "16");
    blur2.appendChild(feGuassianBlur2);

    const circle0 = document.createElementNS(svgNS, "circle") as SVGGeometryElement;
    circle0.id = "hotspot-ring0";
    circle0.setAttribute("cx", "48");
    circle0.setAttribute("cy", "48");
    circle0.setAttribute("r",  "16");
    circle0.style.opacity = "0.45";
    circle0.setAttribute("stroke", "#a9e3ff");
    circle0.setAttribute("stroke-width", "1");
    circle0.setAttribute("fill", "transparent");

    const circle1 = document.createElementNS(svgNS, "circle") as SVGGeometryElement;
    circle1.id = "hotspot-ring1";
    circle1.setAttribute("filter", "url(#blur0)");
    circle1.setAttribute("cx", "48");
    circle1.setAttribute("cy", "48");
    circle1.setAttribute("r",  "16");
    circle1.style.opacity = "0.65";
    circle1.setAttribute("stroke", "#a9e3ff");
    circle1.setAttribute("stroke-width", "1");
    circle1.setAttribute("fill", "transparent");

    const circle2 = document.createElementNS(svgNS, "circle") as SVGGeometryElement;
    circle2.id = "hotspot-ring2";
    circle2.setAttribute("filter", "url(#blur1)");
    circle2.setAttribute("cx", "48");
    circle2.setAttribute("cy", "48");
    circle2.setAttribute("r",  "16");
    circle2.style.opacity = "1.0";
    circle2.setAttribute("stroke", "#4cc7f4");
    circle2.setAttribute("stroke-width", "2");
    circle2.setAttribute("fill", "transparent");

    const circle3 = document.createElementNS(svgNS, "circle") as SVGGeometryElement;
    circle3.id = "hotspot-ring3";
    circle3.setAttribute("filter", "url(#blur2)");
    circle3.setAttribute("cx", "48");
    circle3.setAttribute("cy", "48");
    circle3.setAttribute("r",  "16");
    circle3.style.opacity = "1.0";
    circle3.setAttribute("stroke", "#4cc7f4");
    circle3.setAttribute("stroke-width", "2");
    circle3.setAttribute("fill", "transparent");

    const circle4 = document.createElementNS(svgNS, "circle") as SVGGeometryElement;
    circle4.id = "hotspot-ring4";
    circle4.setAttribute("cx", "48");
    circle4.setAttribute("cy", "48");
    circle4.setAttribute("r", "7");
    circle4.setAttribute("stroke", "white");
    circle4.setAttribute("stroke-width", "1");
    circle4.setAttribute("fill", "white");
    circle4.style.cursor = "pointer";

    const circle5 = document.createElementNS(svgNS, "circle") as SVGGeometryElement;
    // circle5.id = "hotspot-ring5";
    circle5.setAttribute("cx", "48");
    circle5.setAttribute("cy", "48");
    circle5.setAttribute("r", "11");
    circle5.setAttribute("stroke", "white");
    circle5.setAttribute("stroke-width", "2");
    circle5.setAttribute("fill", "transparent");
    circle5.style.cursor = "pointer";

    svg.appendChild(blur0);
    svg.appendChild(blur1);
    svg.appendChild(blur2);
    svg.appendChild(circle0);
    svg.appendChild(circle1);
    svg.appendChild(circle2);
    svg.appendChild(circle3);
    svg.appendChild(circle4);
    svg.appendChild(circle5);
    parent.appendChild(svg);
  }

  private _createHeadlineHotspotGraphic(parent:HTMLDivElement, info:CarModelHeadlineHotspotInfo):void {
    parent.classList.add('hotspot-headline') 
    parent.style.pointerEvents = "none";
    // parent.style.overflow = "hidden";

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg") as SVGElement;
    svg.setAttribute("width", "24");
    svg.setAttribute("height", "24");
    // svg.style.display = "inline-block";
    // svg.style.margin = "-36px 0 0 -36px";

    const circle = document.createElementNS(svgNS, "circle") as SVGGeometryElement;
    circle.setAttribute("cx", "12");
    circle.setAttribute("cy", "12");
    circle.setAttribute("r", "6");
    circle.setAttribute("stroke", "white");
    circle.setAttribute("stroke-width", "2");
    circle.setAttribute("fill", "transparent");

    const span = document.createElement("span");
    span.innerText = info.label;
    // span.style.display = "block";
    span.style.fontFamily = "Volks";
    span.style.fontSize = "20px";
    span.style.color = "white";
    span.style.position = "relative";
    span.style.top = "-5px";
    span.style.marginLeft = "5px"
    span.style.whiteSpace = "nowrap";;
    span.style.textShadow = "0 0 5px black";

    svg.appendChild(circle);
    parent.appendChild(svg);
    parent.appendChild(span);

    if (info.description) {
      const br = document.createElement("br");
      parent.appendChild(br);

      const span2 = document.createElement("span");
      span2.innerText = info.description;
      span2.style.fontFamily = "Volks";
      span2.style.fontSize = "15px";//"1.5vh";
      span2.style.color = "white";
      span2.style.position = "relative";
      span2.style.top = "-5px";
      span2.style.marginLeft = "30px";
      span2.style.whiteSpace = "nowrap";
      span2.style.textShadow = "0 0 5px black";

      parent.appendChild(span2);
    }
  }

  private _createInteractionHotspotGraphic(parent:HTMLDivElement, info:CarModelInteractionHotspotInfo):void {
    parent.style.pointerEvents = "none";

    // const container = document.createElement("div");
    // container.style.width = "28px";
    // container.style.height = "28px";
    // container.style.borderRadius = "48%";
    // container.style.border = "2px solid white";
    // container.style.background = "rgba(255, 255, 255, 0.3)";
    // container.style.position = "relative";
    // container.style.top = "-14px";
    // container.style.left = "-14px";

    // const img = document.createElement("img");
    // img.src = `${HASH}/${info.icon}`;
    // img.style.transform = "scale(1.2)";

    // container.appendChild(img);
    // parent.appendChild(container);

    parent.classList.add('hotspot')

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg") as SVGElement;
    svg.setAttribute("width", "96");
    svg.setAttribute("height", "96");
    svg.style.margin = "-36px 0 0 -36px";
    svg.style.transform = "scale(0.85)";

    const circle4 = document.createElementNS(svgNS, "circle") as SVGGeometryElement;
    circle4.id = "hotspot-ring4";
    circle4.setAttribute("cx", "48");
    circle4.setAttribute("cy", "48");
    circle4.setAttribute("r", "7");
    circle4.setAttribute("stroke", "white");
    circle4.setAttribute("stroke-width", "1");
    circle4.setAttribute("fill", "transparent");//white");
    // circle4.style.cursor = "pointer";

    const circle5 = document.createElementNS(svgNS, "circle") as SVGGeometryElement;
    circle5.id = "hotspot-ring5";
    circle5.setAttribute("cx", "48");
    circle5.setAttribute("cy", "48");
    circle5.setAttribute("r", "11");
    circle5.setAttribute("stroke", "white");
    circle5.setAttribute("stroke-width", "2");
    circle5.setAttribute("fill", "transparent");
    // circle5.style.cursor = "pointer";

    svg.appendChild(circle4);
    svg.appendChild(circle5);
    parent.appendChild(svg);
  }

  public toggleHotspots() {
    if (this._hotspotsVisible) {
      this.hideHotspots();
    } else {
      this.showHotspots();
    }
    HotspotController.event.emit('hotspot')
  }

  public clearBindings():void {
    this._hotspotBinding = {};
  }

  public bindHotspot(name:string, callback:(selected:boolean)=>void) {
    this._hotspotBinding[name] = callback;
  }

  public bindHotspotClose(callback:()=>void):void {
    this._hotspotBindingClose = callback;
  }

  public addContentDiv(name:string, contentDiv:any):void {
    const hotspot = this._hotspots.find(h => h.name === name);
    const element = document.getElementById(hotspot.id);
    contentDiv.id = `${name}_content`;
    element.appendChild(contentDiv);
  }
}

export interface IHotspotData {
  hotspotName: string,
  name: string,
  type: "picture" | "video" | "text",
  content: IHotspotDataContent[]
}

export interface IHotspotDataContent {
  picture: string,
  header: string,
  text: string|string[]
}

export const loadHotspots = async ():Promise<{car:string, data:IHotspotData[]}[]> => {
  const res = await axios.get(`./${HASH}/hotspotData.json`);
  const hotspotData = Object.keys(res.data).map(k => ({car: k, data: res.data[k] as IHotspotData[]}));
  
  hotspotData.forEach(h => {
    h.data.forEach(d => {
      d.content.forEach(c => {
        c.picture = c.picture.replace("/info", `./${HASH}/info`)
      })
    });
  })
  
  console.log("hotspotData", hotspotData);

  return hotspotData;
}